import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, Output, Input, AfterViewInit, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SecurityController } from '@shared/src/controllers/security/security.controller';
import { HImage } from '@shared/src/datatypes/HImage';
import { FileUpDownLoadDto } from '@shared/src/dtos/base/FileUpDownLoadDto';
import { FileTypes } from '@shared/src/enums/FileTypes';
import { AlertService } from '@shared/src/services/alert/alert.service';
import SignaturePad from 'signature_pad';



@Component({
  selector: 'shared-signature-component',
  templateUrl: './shared.signature.component.html',
  styleUrls: ['./shared.signature.component.scss'],
})
export class SharedSignatureComponent implements OnInit, OnDestroy, AfterViewInit {


  private static readonly ATTACH_ACCEPTED_TYPES: string[] = ['image/png', 'image/jpeg'];
  private static readonly ATTACH_MAX_SIZE_MB: number = 2;
  // Píxels amb els tres canals per sobre d'aquest valor es consideren fons blanc i es fan transparents
  private static readonly ATTACH_WHITE_THRESHOLD: number = 230;

  constructor(protected activeroute: ActivatedRoute,
    @Inject('SecurityController') public securityController: SecurityController,
    @Inject('AlertService') protected alertService: AlertService,
    protected translateService: TranslateService) {

    this.securityController.getStoredUserDto().subscribe(data => {
      if (data != null)
        this.readonly = data.isReadOnly;
    })
  }

  private _readonly: boolean = false;
  @Input() public set readonly(value: boolean) {
    if (this._readonly === value)
      return;

    this._readonly = value;
    if (this._readonly && this.signaturePad != null)
      this.signaturePad.off();

  }
  public get readonly(): boolean {
    return this._readonly;
  }

  @ViewChild('canvas') private signaturePadElement: ElementRef;

  private _fileSignatureBytes: any[];
  @Input() public set fileSignatureBytes(value: any[]) {
    if (this._fileSignatureBytes === value)
      return;

    this._fileSignatureBytes = value;
    if (value == null)
      this.onClearSignature();
    else
      this.prepararImatge();

  }
  public get fileSignatureBytes(): any[] {
    return this._fileSignatureBytes;
  }

  private _fileSignatureType: FileTypes = FileTypes.png;
  @Input() public set fileSignatureType(value: FileTypes) {
    if (this._fileSignatureType === value)
      return;

    this._fileSignatureType = value;

  }
  public get fileSignatureType(): FileTypes {
    return this._fileSignatureType;
  }

  public drawSignature() {

    if (this.signaturePadElement == null || this.fileSignatureBytes == null || this.fileSignatureBytes.length == 0 || this.fileSignatureType == null || this.fileSignatureType == FileTypes.None) {
      this.onClearSignature();
      return;
    }

    if (this.signaturePad != null)
      this.signaturePad.on();

    let base64 = "";

    let binary = '';
    let bytes = new Uint8Array(this.fileSignatureBytes);
    let len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    base64 = window.btoa(binary);

    let valueBase64: string = "";

    if (base64 != "") {
      valueBase64 = 'data:image/' + this.fileSignatureType + ';base64,' + base64;
    } else {
      valueBase64 = 'data:image/' + this.fileSignatureType + ';base64,' + this.fileSignatureBytes;
    }

    let image = new Image();
    image.onload = () => {
      this.drawImageFitted(image);
    };
    image.src = valueBase64;
  }

  // Dibuixa la imatge al canvas escalada per cabre-hi sencera, mantenint la proporció i centrada
  private drawImageFitted(image: HTMLImageElement) {
    if (this.signaturePadElement == null || image == null || image.width == 0 || image.height == 0)
      return;

    let canvas: HTMLCanvasElement = this.signaturePadElement.nativeElement;
    let ratio = Math.min(canvas.width / image.width, canvas.height / image.height);
    let width = image.width * ratio;
    let height = image.height * ratio;
    let ctx = canvas.getContext('2d');
    ctx.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  }

  // Fa transparent el fons (quasi) blanc d'una firma escanejada o fotografiada
  private removeWhiteBackground() {
    let canvas: HTMLCanvasElement = this.signaturePadElement.nativeElement;
    let ctx = canvas.getContext('2d');
    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let data = imageData.data;
    let threshold = SharedSignatureComponent.ATTACH_WHITE_THRESHOLD;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > threshold && data[i + 1] > threshold && data[i + 2] > threshold)
        data[i + 3] = 0;
    }
    ctx.putImageData(imageData, 0, 0);
  }

  @Input() public allowAttach: boolean = true;

  // Permet a l'usuari adjuntar una imatge com a firma. Es converteix a PNG a través del canvas
  // i s'emet per endDrawSignature igual que una firma dibuixada.
  public onAttachImage(event: Event) {
    let input = event.target as HTMLInputElement;
    let file: File = input.files != null && input.files.length > 0 ? input.files[0] : null;
    input.value = '';
    if (file == null || this.readonly)
      return;

    if (SharedSignatureComponent.ATTACH_ACCEPTED_TYPES.indexOf(file.type) < 0) {
      this.showAttachError('SIGNATURE.ATTACH.INVALIDTYPE');
      return;
    }
    if (file.size > SharedSignatureComponent.ATTACH_MAX_SIZE_MB * 1024 * 1024) {
      this.showAttachError('SIGNATURE.ATTACH.TOOBIG', { value: SharedSignatureComponent.ATTACH_MAX_SIZE_MB });
      return;
    }

    let reader = new FileReader();
    reader.onload = () => {
      let image = new Image();
      image.onload = () => {
        if (this.signaturePad == null)
          this.initSignature();
        if (this.signaturePad == null)
          return;

        this.signaturePad.clear();
        this.drawImageFitted(image);
        this.removeWhiteBackground();
        this.endDrawing(null);
      };
      image.onerror = () => {
        this.showAttachError('SIGNATURE.ATTACH.INVALIDTYPE');
      };
      image.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private showAttachError(key: string, params?: Object) {
    this.translateService.get(key, params).subscribe(data => {
      this.alertService.error(data);
    });
  }

  @Output() clearSignature: EventEmitter<boolean> = new EventEmitter();
  public signaturePad: SignaturePad;
  onClearSignature() {
    if (this.signaturePad == null) {
      this.initSignature();
    } else
      this.signaturePad.clear();

    if (this.signaturePad != null) {
      this.signaturePad.on();
    }
    this.clearSignature.next(true);
  }

  endDrawing(event: Event) {

    if (this.signaturePad == null) {
      return;
    }

    let signature = this.signaturePad.toDataURL("image/png");
    if (signature == null) {
      this.fileSignatureBytes = null;
      this.fileSignatureType = null;
    } else {

      let base64DataSignature = signature.split(',')[1];
      this.fileSignatureBytes = FileUpDownLoadDto.getByteArrayFromString(base64DataSignature);
      this.fileSignatureType = FileTypes.png;
    }

    this.endDrawSignature.next({ signbytes: this.fileSignatureBytes, bytestype: this.fileSignatureType });

  }

  @Output() endDrawSignature: EventEmitter<any> = new EventEmitter();

  initSignature() {
    if (this.signaturePadElement != null && this.signaturePadElement.nativeElement != null) {
      this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement);
      this.drawSignature();
      if (this.signaturePad != null && this._readonly)
        this.signaturePad.off();
    }
  }
  ngOnInit(): void {
  }

  ngAfterViewInit() {
    this.initSignature();
  }


  ngOnDestroy() {
  }

  public imageSrc: string;
  prepararImatge() {
    this.imageSrc = HImage.toPng(this.fileSignatureBytes)
  }
}
